import type {GroupingFn, ChoiceWithContext, EntryWithContext} from './types'
import {trimFileName} from '../../../utils/trim-file-name'
import {getToolGroupingKey, TOOL_NAMES} from '../../../constants/tool-names'

/**
 * Type guard to check if a session log item is a choice
 */
function isChoiceItem(item: EntryWithContext): item is ChoiceWithContext {
  return item.type === 'choice'
}

/**
 * Checks if a run_setup choice is a subagent marker (start/end) based on details.type.
 * Subagent markers should not be grouped under "Setting up environment".
 */
function isSubagentMarker(item: ChoiceWithContext): boolean {
  const toolCall = item.choice?.delta?.tool_calls?.[0]
  if (!toolCall?.function?.arguments) return false
  if (toolCall.function.name !== TOOL_NAMES.RUN_SETUP) return false
  try {
    const args = JSON.parse(toolCall.function.arguments) as {details?: {type?: string}}
    const detailsType = args.details?.type
    return detailsType === 'subagent_start' || detailsType === 'subagent_end'
  } catch {
    return false
  }
}

/**
 * Helper to check if a view tool call has a path (viewing a file) vs no path (viewing repository)
 * Uses the exact same logic as getCommandTitle in Tool.tsx: `!!trimFileName(path)?.length`
 */
function hasViewPath(choiceWithContext: ChoiceWithContext): boolean {
  const {choice} = choiceWithContext
  const toolCall = choice?.delta?.tool_calls?.[0]

  if (!toolCall) return false

  const argsString = toolCall.function?.arguments
  if (!argsString) return false

  try {
    const args = JSON.parse(argsString) as {path?: string}
    // Match the exact logic from Tool.tsx getCommandTitle
    const hasPath = !!trimFileName(args.path || '')?.length
    return hasPath
  } catch {
    return false
  }
}

/**
 * Extracts the tool name from a choice.
 */
function getToolName(choiceWithContext: ChoiceWithContext): string | null {
  const {choice} = choiceWithContext
  const delta = choice?.delta

  if (!delta?.tool_calls || delta.tool_calls.length === 0) {
    return null
  }

  const toolCall = delta.tool_calls[0]
  const functionName = toolCall?.function?.name ?? toolCall?.custom?.name ?? null

  if (!functionName) {
    return null
  }

  // For str_replace_editor or edit tools, the actual command is in the arguments
  // This matches the logic in Tool.tsx: `const command = name === TOOL_NAMES.EDIT ? 'str_replace' : strReplaceEditorArgs.command || name`
  if (functionName === TOOL_NAMES.STR_REPLACE_EDITOR || functionName === TOOL_NAMES.EDIT) {
    if (functionName === TOOL_NAMES.EDIT) {
      return 'str_replace'
    }
    // For str_replace_editor, use the command from args if available
    const argsString = toolCall?.function?.arguments ?? null
    if (argsString) {
      try {
        const args = JSON.parse(argsString) as {command?: string}
        return args.command || functionName
      } catch {
        return functionName
      }
    }
    return functionName
  }

  return functionName
}

/**
 * Grouping function that groups consecutive tool calls of the same type.
 * Non-choice items (commands, etc.) are never grouped and break any active grouping.
 */
export const groupByConsecutiveToolName: GroupingFn = (item, lastGroup) => {
  // Non-choice items (commands, etc.) are always standalone and break grouping
  if (!isChoiceItem(item)) {
    return {shouldGroup: false}
  }

  const currentToolName = getToolName(item)

  if (!currentToolName) {
    return {shouldGroup: false}
  }

  const groupingKey = getToolGroupingKey(currentToolName)
  if (groupingKey === null) {
    return {shouldGroup: false}
  }

  const isSetupTool = groupingKey === 'setup'

  // Subagent markers use run_setup but should not be grouped under "Setting up environment"
  if (isSetupTool && isSubagentMarker(item)) {
    return {shouldGroup: false}
  }

  const lastGroupToolName = lastGroup?.metadata?.toolName as string | undefined
  const lastGroupIsSetupTool = lastGroup?.metadata?.isSetupTool as boolean | undefined

  // Group all setup tools together regardless of specific name
  if (isSetupTool && lastGroupIsSetupTool && lastGroup) {
    return {
      shouldGroup: true,
      groupId: lastGroup.id,
      groupMetadata: {toolName: lastGroupToolName, isSetupTool: true},
    }
  }

  const isViewRepository = currentToolName === 'view' && !hasViewPath(item)

  // Check if we should continue the last group by comparing tool names
  const lastGroupIsViewRepository = (lastGroup?.metadata?.isViewRepository as boolean | undefined) ?? false

  // Don't group view repository with file views, and vice versa
  // But still allow starting a new group for the new view type
  if (currentToolName === 'view' && lastGroupToolName === 'view') {
    // Both are view operations, but we need to check if they're the same type
    if (isViewRepository !== lastGroupIsViewRepository) {
      // Start a new group instead of returning shouldGroup: false
      return {
        shouldGroup: true,
        groupId: crypto.randomUUID(),
        groupMetadata: {toolName: currentToolName, isViewRepository, isSetupTool},
      }
    }
  }

  const shouldContinueGroup = lastGroupToolName === currentToolName && !lastGroup?.metadata?.standalone

  if (shouldContinueGroup && lastGroup) {
    return {
      shouldGroup: true,
      groupId: lastGroup.id, // Reuse the group's ID to continue the group
      groupMetadata: {toolName: currentToolName, isViewRepository, isSetupTool},
    }
  }

  // Start a new group - allow any tool with a name to be grouped
  if (currentToolName) {
    return {
      shouldGroup: true,
      // Generate deterministic ID based on first entry's identifiers to maintain stable React keys across re-renders
      // this is safe as we never group non-choice items
      groupId: `${currentToolName}-${item.entryId}-${item.choiceIndex}`,
      groupMetadata: {toolName: currentToolName, isViewRepository, isSetupTool},
    }
  }

  return {shouldGroup: false}
}
